import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { IonAlert } from "@ionic/react";
import { Badge, Divider, Link, Stack, ThemeProvider } from "@mui/material";
import { AppVersionInfo } from "@src/appV2/Accounts/AppVersionInfo/AppVersionInfo";
import { ACCOUNTS_CLIPBOARD_RESUME_FULL_PATH } from "@src/appV2/Accounts/ClipboardResume/paths";
import { LogoutConfirmationBottomSheet } from "@src/appV2/Accounts/Logout/LogoutConfirmationBottomSheet";
import { REFER_AND_EARN_WITH_ACCOUNTS_PATH } from "@src/appV2/Accounts/ReferAndEarn/paths";
import { WORKPLACE_REFERRALS_WITH_ACCOUNTS_PATH } from "@src/appV2/Accounts/WorkplaceReferrals";
import { useWorkerToWorkplaceAffiliateExperiment } from "@src/appV2/Accounts/WorkplaceReferrals/hooks/useWorkerToWorkplaceAffiliateExperiment";
import { DeprecatedGlobalAppV1Paths, RootPaths } from "@src/appV2/App/paths";
import { OrientationMenuItem } from "@src/appV2/ClipboardOrientation/components/OrientationMenuItem";
import { CLIPBOARD_SCORE } from "@src/appV2/ClipboardScore/constants";
import { DEBUG_BASE_PATH } from "@src/appV2/Debug/paths";
import {
  DeploymentEnvironmentName,
  environmentConfig,
  isDevelopmentNodeEnvironment,
} from "@src/appV2/environment";
import { CbhFeatureFlag, useCbhFlag, useCbhFlags } from "@src/appV2/FeatureFlags";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { isZendeskMessagingUnread } from "@src/appV2/lib/ZendeskMessaging/utils";
import { useATeamStatus } from "@src/appV2/Rankings/useATeamStatus";
import { AccountPageBannersContainer } from "@src/appV2/redesign/Accounts/Banners/AccountPageBannersContainer";
import { ACCOUNTS_BASE_PATH, ACCOUNT_UPDATES_FULL_PATH } from "@src/appV2/redesign/Accounts/paths";
import { PAYOUT_SETTINGS_FULL_PATH } from "@src/appV2/redesign/Accounts/PayoutSettings/paths";
import { ACCOUNT_ROLES_FULL_PATH } from "@src/appV2/redesign/Accounts/Roles/paths";
import { useIsAccountPageRedesignEnabled } from "@src/appV2/redesign/Accounts/useIsAccountPageRedesignEnabled";
import { CLIPBOARD_SCORE_FULL_PATH } from "@src/appV2/redesign/ClipboardScore/utils/paths";
import { isWorkerInviteRequired } from "@src/appV2/redesign/ClipboardScore/utils/utils";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { PRIVACY_POLICY_URL } from "@src/appV2/redesign/constants/urls";
import { useHasActionRequiredForMandatoryRequirements } from "@src/appV2/redesign/Documents/api/useHasActionRequiredForMandatoryRequirements";
import { DOCUMENTS_BASE_PATH } from "@src/appV2/redesign/Documents/paths";
import { Navbar } from "@src/appV2/redesign/Navbar";
import { NOTIFICATIONS_PREFERENCE_PATH_FULL } from "@src/appV2/redesign/NotificationPreferences/paths";
import { useGetPlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/api/useGetPlacementCandidate";
import { useIsJobsEnabled } from "@src/appV2/redesign/PlacementCandidate/useIsJobsEnabled";
import { useIsSurfaceShiftsEnabled } from "@src/appV2/redesign/ShiftDiscovery/useIsSurfaceShiftsEnabled";
import { ImprovedTierBottomSheet } from "@src/appV2/redesign/Tiers/BottomSheets/ImprovedTierBottomSheet";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { SKILLS_ASSESSMENT_BASE_PATH } from "@src/appV2/SkillsAssessment/paths";
import { useShouldBeOnboardedToStripe } from "@src/appV2/Stripe/useShouldBeOnboardedToStripe";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useUpdateWorkerCache } from "@src/appV2/Worker/useUpdateWorkerCache";
import { USER_EVENTS } from "@src/constants";
import { logApiFailureEvent } from "@src/lib/analytics";
import { useDefinedEnv } from "@store/helperHooks/sessionHelpers";
import { Store } from "@store/store.model";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, useHistory, useRouteMatch } from "react-router-dom";
import request from "superagent";

import { setNotificationPreference } from "./api";
import { MyAccountProps } from "./model";
import { useIsSavingsAccountViewEnabled } from "../savings/featureFlags";
import { getAuthHeader } from "../superagent";

export function MyAccount(props: MyAccountProps) {
  const { logout, isLoggingOut } = props;
  const history = useHistory();
  const match = useRouteMatch();
  const logoutModal = useModalState();
  const [isClosed, setClosed] = useState<boolean>(false);
  const [pnChecked, setPNChecked] = useState(false);
  const [pushFlag, setPushFlag] = useState<boolean>(false);
  const [isNotificationAlertShown, setNotificationAlertShown] = useState<boolean>(true);
  const worker = useDefinedWorker();
  const { userId } = worker;
  const env = useDefinedEnv();
  const { unreadCount } = useSelector((state: Store) => state.notificationStore);

  const updateWorkerCache = useUpdateWorkerCache();

  const shouldBeOnboardedInPaymentService = useShouldBeOnboardedToStripe();
  const { isATeamOffExperimentEnabled } = useATeamStatus();

  const hasActionRequiredForMandatoryRequirements = useHasActionRequiredForMandatoryRequirements();
  const isSurfaceShiftsEnabled = useIsSurfaceShiftsEnabled();

  const ldFlags = useCbhFlags();

  const workplaceReferralsConfig = ldFlags[CbhFeatureFlag.WORKPLACE_REFERRALS] ?? {};
  const isWorkerToWorkplaceReferralsEnabled = workplaceReferralsConfig.workerToWorkplace ?? false;
  const isWorkerToWorkplaceAffiliateProgramEnabled = useWorkerToWorkplaceAffiliateExperiment();

  const flagSettings = { defaultValue: false };

  const isJobsEnabled = useIsJobsEnabled();
  const isPassportEnabled = useCbhFlag(
    CbhFeatureFlag.PLACEMENT_CANDIDATE_RESUME_PARSING,
    flagSettings
  );
  const mayShowPassportMenuItem = isJobsEnabled && isPassportEnabled;
  const { data: placementCandidate } = useGetPlacementCandidate(
    { workerId: userId },
    { enabled: mayShowPassportMenuItem }
  );
  const tiersConfig = useCbhFlag(CbhFeatureFlag.CONFIG_TIERS, {
    defaultValue: { enabled: false },
  });

  const isInviteRequired = isWorkerInviteRequired(worker);

  const showClipboardScorePage =
    !tiersConfig.enabled && !isInviteRequired && isATeamOffExperimentEnabled;

  const showRatingsPage = !tiersConfig.enabled && !isInviteRequired && !isATeamOffExperimentEnabled;

  const showDebugPageMenuItemForTesting =
    useCbhFlag(CbhFeatureFlag.SHOW_DEBUG_PAGE_MENU_ITEM_IN_MY_ACCOUNT, flagSettings) ||
    isDevelopmentNodeEnvironment();

  const showAppUpdatePageMenuItemForTesting =
    isCapacitorPlatform() &&
    (worker.isTestAccount ||
      environmentConfig.REACT_APP_ENVIRONMENT_NAME !== DeploymentEnvironmentName.PRODUCTION);

  const isPayoutSettingsEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_NEW_PAYOUT_SETTINGS, {
    defaultValue: false,
  });

  const isSavingsAccountViewEnabled = useIsSavingsAccountViewEnabled();

  useEffect(() => {
    if (!env) {
      return;
    }
    setPNChecked(Boolean(env?.notificationPreferences?.push));
    setPushFlag(Boolean(env?.PushNotificationOnAccountTab));
    setClosed(true);
  }, [env]);

  useEffect(() => {
    setNotificationAlertShown(Boolean(worker?.isNotificationAlertShown));
  }, [worker]);

  useLogEffect(USER_EVENTS.VIEWED_ACCOUNT);

  const manageLicensesTitleText = isSurfaceShiftsEnabled
    ? "Manage Roles & Licenses"
    : "Manage Licenses & Qualifications";

  const manageLicensesSecondaryText = isSurfaceShiftsEnabled
    ? "Manage roles and licenses"
    : "Manage licenses and qualifications";

  const logMenuItemTapped = (menuItemName: string) => {
    logEvent(USER_EVENTS.TAPPED_ACCOUNT_MENU_ITEM, { menuItemName });
  };

  const setNotificationPopUpShown = async () => {
    const { body: agent } = await request
      .post(`${environmentConfig.REACT_APP_BASE_API_URL}/agentProfile/setNotificationAlertShown`)
      .set(await getAuthHeader());

    updateWorkerCache({
      ...agent,
    });
  };

  const dismissAlert = async () => {
    try {
      setClosed(false);
      setNotificationPopUpShown();
      await setNotificationPreference({
        notificationPreference: { key: "push", value: true },
      });
    } catch (error) {
      logApiFailureEvent(error);
    }
  };

  const scrollRef = useRef<HTMLDivElement>(null);

  const isAccountPageRedesignEnabled = useIsAccountPageRedesignEnabled();

  if (isAccountPageRedesignEnabled) {
    return <Redirect to={ACCOUNTS_BASE_PATH} />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Account"
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
          hideBackButton
        />

        <PageContent ref={scrollRef}>
          <AccountPageBannersContainer workerId={userId} />

          <List
            component="div"
            ref={scrollRef}
            sx={{
              "& li": {
                height: 62,
              },
            }}
          >
            {showAppUpdatePageMenuItemForTesting && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("App Updates");
                      history.push(ACCOUNT_UPDATES_FULL_PATH);
                    }}
                  >
                    <ListItemText primary="App Updates" secondary="Check for and install updates" />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}

            {showDebugPageMenuItemForTesting && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("Debug");
                      history.push(DEBUG_BASE_PATH);
                    }}
                  >
                    <ListItemText primary="Debug" secondary="Internal testing" />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}
            {showClipboardScorePage && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("Clipboard Score");
                      history.push(CLIPBOARD_SCORE_FULL_PATH);
                    }}
                  >
                    <ListItemText
                      primary="Clipboard Score"
                      secondary="View your score and how it moves with your activity"
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}
            {showRatingsPage && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("Account Status");
                      history.push(DeprecatedGlobalAppV1Paths.RATINGS);
                    }}
                  >
                    <ListItemText primary="Account Status" secondary={CLIPBOARD_SCORE} />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}
            {isDefined(placementCandidate?.passportUrl) && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("Clipboard Resume");
                      history.push(`${ACCOUNTS_CLIPBOARD_RESUME_FULL_PATH}`);
                    }}
                  >
                    <ListItemText primary="Your Updated Resume" secondary="Manage your resume" />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}
            <ListItem>
              <ListItemButton
                hideCallToActionIcon
                onClick={() => {
                  logMenuItemTapped("Earnings");
                  history.push(`${match.url}/payroll`);
                }}
              >
                <ListItemText
                  primary="Earnings"
                  secondary="View your earnings"
                  sx={{
                    color: (theme) =>
                      shouldBeOnboardedInPaymentService ? theme.palette.error.main : undefined,
                  }}
                />
              </ListItemButton>
            </ListItem>
            <Divider />
            {isSavingsAccountViewEnabled && (
              <ListItem divider>
                <ListItemButton
                  hideCallToActionIcon
                  onClick={() => {
                    logMenuItemTapped("Savings");
                    history.push(`${match.url}/savings/configuration/goals`);
                  }}
                >
                  <ListItemText primary="Savings" secondary="Reach your financial goals" />
                </ListItemButton>
              </ListItem>
            )}
            {isPayoutSettingsEnabled && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("Payout Settings");
                      history.push(PAYOUT_SETTINGS_FULL_PATH);
                    }}
                  >
                    <ListItemText
                      primary="Payout Settings"
                      secondary="Configure how you get paid"
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}
            <ListItem>
              <ListItemButton
                hideCallToActionIcon
                onClick={() => {
                  logMenuItemTapped("Documents");
                  history.push(`${RootPaths.APP_V2_HOME}/${DOCUMENTS_BASE_PATH}`);
                }}
              >
                <ListItemText
                  primary="Documents for Flex Shifts"
                  secondary={
                    hasActionRequiredForMandatoryRequirements
                      ? "Some documents are missing or expired!"
                      : "Manage your documents"
                  }
                  sx={{
                    "& .MuiListItemText-secondary": {
                      color: hasActionRequiredForMandatoryRequirements
                        ? (theme) => theme.palette.error.main
                        : undefined,
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemButton
                hideCallToActionIcon
                onClick={() => {
                  logMenuItemTapped(manageLicensesTitleText);
                  history.push(
                    isSurfaceShiftsEnabled ? ACCOUNT_ROLES_FULL_PATH : `${match.url}/licenses`
                  );
                }}
              >
                <ListItemText
                  primary={manageLicensesTitleText}
                  secondary={manageLicensesSecondaryText}
                />
              </ListItemButton>
            </ListItem>
            <Divider />
            {ldFlags[CbhFeatureFlag.LICENSE_RECIPROCITY] && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("Get Licensed in Another State");
                      history.push(`${match.url}/license-reciprocity`);
                    }}
                  >
                    <ListItemText
                      primary="Get Licensed in Another State"
                      secondary="Apply for licenses in other states through reciprocity"
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}
            <ListItem
              secondaryAction={
                isZendeskMessagingUnread() && <Badge color="error" badgeContent={unreadCount} />
              }
            >
              <ListItemButton
                hideCallToActionIcon
                onClick={() => {
                  logMenuItemTapped("Get Help");
                  // TODO:
                  // We've merged the separate 'logout' and 'help center' tap events into a single event named 'tapped account menu item'
                  // Once we update our Metabase metrics (if having), which currently relies on these events, we can safely remove this
                  logEvent(USER_EVENTS.OPENED_HELP_CENTER);
                  history.push(`${match.url}/needhelp`);
                }}
              >
                <ListItemText
                  primary="Get Help"
                  secondary="Contact support or view help articles"
                />
              </ListItemButton>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemButton
                hideCallToActionIcon
                onClick={() => {
                  logMenuItemTapped("Profile");
                  history.push(`${match.url}/profile`);
                }}
              >
                <ListItemText primary="Profile" secondary="Manage profile and address" />
              </ListItemButton>
            </ListItem>
            <Divider />
            {isWorkerToWorkplaceReferralsEnabled && isWorkerToWorkplaceAffiliateProgramEnabled && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("Facility Affiliate Program");
                      history.push(WORKPLACE_REFERRALS_WITH_ACCOUNTS_PATH);
                    }}
                  >
                    <ListItemText
                      primary="Workplace Affiliate Program"
                      secondary="Earn $1,000 for every workplace you refer"
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}
            {isWorkerToWorkplaceReferralsEnabled && !isWorkerToWorkplaceAffiliateProgramEnabled && (
              <>
                <ListItem>
                  <ListItemButton
                    hideCallToActionIcon
                    onClick={() => {
                      logMenuItemTapped("Refer and Earn");
                      history.push(REFER_AND_EARN_WITH_ACCOUNTS_PATH);
                    }}
                  >
                    <ListItemText
                      primary="Refer a Friend or Workplace"
                      secondary="Earn money on your referrals"
                    />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </>
            )}
            <Divider />
            <ListItem>
              <ListItemButton
                hideCallToActionIcon
                onClick={() => {
                  logMenuItemTapped("Work with Friends");
                  history.push(`${match.url}/friends`);
                }}
              >
                <ListItemText
                  primary="Work with Friends"
                  secondary="Connect with friends to see their upcoming shifts"
                />
              </ListItemButton>
            </ListItem>
            <OrientationMenuItem
              worker={worker}
              onClick={() => {
                logMenuItemTapped("Skills Assessment");
                history.push(SKILLS_ASSESSMENT_BASE_PATH);
              }}
            />

            <ListItem>
              <ListItemButton
                hideCallToActionIcon
                onClick={() => {
                  logMenuItemTapped("Notification Preferences");
                  history.push(NOTIFICATIONS_PREFERENCE_PATH_FULL);
                }}
              >
                <ListItemText
                  primary="Notification Preferences"
                  secondary="Manage your notifications"
                />
              </ListItemButton>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemButton
                hideCallToActionIcon
                onClick={() => {
                  logMenuItemTapped("Logout");
                  // TODO:
                  // We've merged the separate 'logout' and 'help center' tap events into a single event named 'tapped account menu item'
                  // Once we update our Metabase metrics (if having), which currently relies on these events, we can safely remove this
                  logEvent(USER_EVENTS.TAPPED_LOGOUT);
                  logoutModal.openModal();
                }}
              >
                <ListItemText primary="Log out" secondary="Sign out of your account" />
              </ListItemButton>
            </ListItem>
            <Divider />
          </List>

          <Stack alignItems="center" marginTop={3}>
            <Link href={PRIVACY_POLICY_URL}>Privacy Policy</Link>
            <AppVersionInfo />
          </Stack>
        </PageContent>

        <Navbar />

        <LogoutConfirmationBottomSheet
          modalState={logoutModal}
          onLogout={async () => {
            logout();
          }}
          isLoading={isLoggingOut}
        />
        <ImprovedTierBottomSheet />

        <IonAlert
          isOpen={!pushFlag && !isNotificationAlertShown && !pnChecked && isClosed}
          onDidDismiss={dismissAlert}
          header="Notifications"
          message="For this app to work, we'll need push notifications to notify you of your upcoming shifts, open shifts and raffles."
          backdropDismiss={false}
          buttons={["Ok"]}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
